import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Award, BarChart3, CalendarDays, Target, Trophy } from "lucide-react";
import { useQuiz } from "../context/QuizContext";
import quizService from "../services/quizService";
import { Page, PageContainer, PageHeader } from "../components/ui/page";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Alert, EmptyState, Skeleton } from "../components/ui/feedback";
import { StatCard, StatusBadge, Table, TableWrap, TD, TH, THead } from "../components/ui/data-display";

const ResultPage = () => {
  const navigate = useNavigate();
  const { userInfo } = useQuiz();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userInfo?.email) return;

    const loadResults = async () => {
      try {
        setLoading(true);
        setError("");
        const [statistics, attempts] = await Promise.all([
          quizService.getUserStatistics(userInfo.email),
          quizService.getUserQuizResults(userInfo.email, 100),
        ]);
        setStats(statistics);
        setHistory(Array.isArray(attempts) ? attempts : []);
      } catch (requestError) {
        setError(requestError.message || "Failed to load results");
      } finally {
        setLoading(false);
      }
    };

    loadResults();
  }, [userInfo?.email]);

  const latest = history[0];
  const totalAttempts = Number(stats?.total_attempts ?? history.length);

  return (
    <Page>
      <PageContainer size="xl">
        <PageHeader
          eyebrow="Performance"
          title="My results"
          description="Track your assessment history and review each completed attempt."
          icon={BarChart3}
          actions={<Button variant="outline" onClick={() => navigate("/checklist")}>Security checklist</Button>}
        />

        {error && <Alert variant="danger" className="mb-5">{error}</Alert>}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {loading ? [1, 2, 3, 4].map(item => <Skeleton key={item} className="h-28" />) : (
            <>
              <StatCard label="Attempts" value={totalAttempts} icon={Target} />
              <StatCard label="Best score" value={`${Math.round(stats?.best_score || 0)}%`} icon={Trophy} tone="success" />
              <StatCard label="Average" value={`${Math.round(stats?.average_score || 0)}%`} icon={Award} />
              <StatCard
                label="Latest"
                value={latest ? `${Math.round(latest.percentage)}%` : "—"}
                description={latest ? new Date(latest.completed_at).toLocaleDateString() : "No attempt yet"}
                icon={CalendarDays}
              />
            </>
          )}
        </div>

        <Card className="mt-5 overflow-hidden">
          <CardHeader>
            <CardTitle>Assessment history</CardTitle>
            <p className="secondary-text">Select an attempt to review the recorded answers and feedback.</p>
          </CardHeader>

          {loading ? (
            <CardContent className="space-y-3">
              {[1, 2, 3].map(item => <Skeleton key={item} className="h-14" />)}
            </CardContent>
          ) : !history.length ? (
            <EmptyState title="No attempts yet" description="Your completed assessments will appear here." />
          ) : (
            <TableWrap>
              <Table>
                <THead>
                  <tr>
                    <TH>Attempt</TH>
                    <TH>Date</TH>
                    <TH>Score</TH>
                    <TH>Status</TH>
                    <TH className="text-right">Action</TH>
                  </tr>
                </THead>
                <tbody className="divide-y divide-border">
                  {history.map((result, index) => (
                    <tr key={result.id} className="interactive-row">
                      <TD className="font-medium">Attempt #{Math.max(1, totalAttempts - index)}</TD>
                      <TD className="text-muted-foreground">{new Date(result.completed_at).toLocaleString()}</TD>
                      <TD>{result.score}/{result.total_questions}</TD>
                      <TD><StatusBadge percentage={result.percentage} /></TD>
                      <TD className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/result/${result.id}`)}>
                          View details
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </TD>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </Card>
      </PageContainer>
    </Page>
  );
};

export default ResultPage;
